/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import * as React from 'react';
import { useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { Button } from '@coursera/cds-core';
import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import type { ClickStatus } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import Submit from 'bundles/authentication/components/Submit';
import { ARKOSE_LOGIN_KEY, LOGIN_REDESIGN_RECAPTCHA_ID, RECAPTCHA_ACTIONS } from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
import TwoFactorAuthInput from 'bundles/authentication/shared/TwoFactorAuthInput';
import * as actions from 'bundles/authentication/shared/actions';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import useBotManager from 'bundles/authentication/shared/hooks/useBotManager';
import type { LoginValues, LoginSharedProps as Props } from 'bundles/authentication/shared/types/sharedTypes';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication/utils/loginUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import { checkForcedSsoLogin } from 'bundles/third-party-auth/api';
import { type CheckForcedSsoLoginResponse } from 'bundles/third-party-auth/types/responseTypes';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);
const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = {
  form: css`
    /* This is an offset of the marginTop below to keep the space above consistent */
    margin-top: -12px;
  `,
  inputContainer: css`
    margin-top: var(--cds-spacing-150);
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
  `,
  experiment: css`
    display: flex;
    justify-content: space-between;
  `,
  item: css`
    :nth-child(1) {
      -ms-grid-row: 1;
    }

    :nth-child(2) {
      -ms-grid-row: 2;
    }

    :nth-child(3) {
      -ms-grid-row: 3;
    }

    :nth-child(4) {
      -ms-grid-row: 4;
    }

    :nth-child(5) {
      -ms-grid-row: 5;
    }
  `,
  reCAPTCHA: css`
    display: flex;
    justify-content: center;

    :nth-of-type(n) .grecaptcha-badge {
      display: none !important;
    }
  `,
  reCAPTCHACheckbox: css`
    margin-top: var(--cds-spacing-100);
  `,
  submit: css`
    margin-top: var(--cds-spacing-150);
  `,
  styleConflictInputFix: css`
    :nth-of-type(n) input {
      border: 0 !important;
      height: unset;
      margin-bottom: 0 !important;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
      line-height: 22px;
      font-size: 16px;
      outline: none;
      box-shadow: none !important;
      color: unset;
    }

    :nth-of-type(n) input:focus:required:invalid:focus {
      color: unset;
    }

    :nth-of-type(n) input::placeholder {
      color: #767676;
      opacity: 1;
    }

    /* Internet Explorer 10-11 */
    :nth-of-type(n) input:-ms-input-placeholder {
      color: #767676;
      opacity: 1;
    }

    /* Edge */
    :nth-of-type(n) input:-webkit-input-placeholder {
      color: #767676;
      opacity: 1;
    }
  `,
};

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type Context = {
  _eventData: EventData;
};
const enterpriseSsoLoginInfoInitialState = {
  isEnterpriseSsoLogin: false,
  redirectUrl: '',
  isLoading: false,
  isRedirecting: false,
};

export const INPUT_DEBOUNCE_TIME = 300;

const LoginForm = (props: Props, { _eventData }: Context) => {
  const { arkoseBotManagerContext } = props;
  const [has2FA, setHas2FA] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ReactNode>();
  const [enterpriseSsoLoginInfo, setEnterpriseSsoLoginInfo] = useState(enterpriseSsoLoginInfoInitialState);
  const input = useRef<HTMLInputElement>(null);

  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');

  const trackEvent = useTracker();
  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  /* tracks click events to submit button regardless of successful or not */
  const handleTracking = (status: ClickStatus) => {
    trackEvent('click_button', {
      button: {
        name: 'login_with_email',
        clickStatus: status,
      },
      modal: { name: 'login' },
    });
  };

  const handleError = (response: $TSFixMe) => {
    const { onError = () => {} } = props;

    resetBotManager();

    const { errorCode: code } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(code)) {
      if (code === 'invalidCredential') {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]());
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
        input.current?.focus();
      } else {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]);
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
      }
    } else {
      onError(response);
    }
  };

  const login = async (values: LoginValues) => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    const { onSuccess } = props;
    if (!values.email || !values.password) {
      handleTracking('invalid');
      return setError(errorMessageFactory().invalidRequest);
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      handleTracking('invalid');
      return setError(isCheckbox ? errorMessageFactory().recaptchaFailed : '');
    }

    setIsRunning(true);

    const payload = {
      ...omit(values, 'one-time-password'),
      ...tokenData,
      ...(values['one-time-password'] && { mfa: values['one-time-password'] }),
    };

    trackAuthAttempts(_eventData, 'login');

    try {
      const response = await actions.login(payload);
      handleTracking('valid');

      return onSuccess(response?.redirectTo as string);
    } catch (error) {
      // The case where 2FA is required is treated as an error in the FE but shouldn't be for the sake of tracking
      if (!(error?.responseJSON?.errorCode === 'twoFactorRequired')) {
        handleTracking('invalid');
      }
      return handleError(error);
    }
  };

  const debouncedCheckForceSSO = debounce((emailField) => {
    const errors = runValidations(emailField, [required, email]);
    if (!errors) {
      setEnterpriseSsoLoginInfo((state) => ({ ...state, isLoading: true }));
      checkForcedSsoLogin(emailField)
        .then((response: CheckForcedSsoLoginResponse) => {
          if (response.isForceSSOLogin) {
            setEnterpriseSsoLoginInfo((state) => ({
              ...state,
              isEnterpriseSsoLogin: true,
              isLoading: false,
              redirectUrl: response.redirectUrl,
            }));
          } else {
            setEnterpriseSsoLoginInfo({ ...enterpriseSsoLoginInfoInitialState });
          }
        })
        .catch(() => {
          setEnterpriseSsoLoginInfo({ ...enterpriseSsoLoginInfoInitialState });
        });
    } else {
      setEnterpriseSsoLoginInfo(() => ({ ...enterpriseSsoLoginInfoInitialState }));
    }
  }, INPUT_DEBOUNCE_TIME);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailField = e.target.value;
    debouncedCheckForceSSO(emailField);
  };

  const onEnterpriseSsoLogin = () => {
    setEnterpriseSsoLoginInfo((state) => ({ ...state, isRedirecting: true }));
    window.location.replace(enterpriseSsoLoginInfo.redirectUrl);
  };

  const { defaultEmail, onForgotPassword } = props;
  const { isEnterpriseSsoLogin, isLoading, isRedirecting } = enterpriseSsoLoginInfo;

  return (
    <Form
      className="rc-LoginForm"
      css={styles.form}
      name="login"
      onSubmit={isEnterpriseSsoLogin ? onEnterpriseSsoLogin : (values) => login(values as LoginValues)}
    >
      <div aria-live="assertive">
        {error && !isRunning && (
          <Alert css={styles.item} type="error">
            {error}
          </Alert>
        )}
      </div>
      <div css={styles.inputContainer}>
        <EmailInput
          css={[styles.styleConflictInputFix, styles.item]}
          autoFocus
          data-e2e="login-email-input"
          defaultValue={defaultEmail}
          ref={input}
          onChange={onEmailChange}
        />
        {!isEnterpriseSsoLogin && (
          <PasswordInput
            css={[styles.styleConflictInputFix, styles.item]}
            data-e2e="login-password-input"
            hint={
              <TrackedLinkButton onClick={onForgotPassword} trackingName="login_form_forgot_password_link">
                {_t('Forgot password?')}
              </TrackedLinkButton>
            }
            placeholder={_t('Enter your password')}
            autoComplete="current-password"
          />
        )}
        {has2FA && (
          <TwoFactorAuthInput
            css={[styles.styleConflictInputFix, styles.item]}
            autoFocus
            data-e2e="verification-code-field"
          />
        )}
        <div
          css={[styles.reCAPTCHA, isCheckbox ? styles.reCAPTCHACheckbox : null]}
          id={LOGIN_REDESIGN_RECAPTCHA_ID}
          data-size={!isCheckbox && 'invisible'}
        />
        {isEnterpriseSsoLogin ? (
          <Button disabled={isLoading} loading={isRedirecting} type="submit" style={{ minWidth: '100%' }}>
            {_t('Continue with SSO')}
          </Button>
        ) : (
          <TrackedSubmit
            css={[styles.submit, styles.item]}
            data-e2e="login-form-submit-button"
            disabled={isRunning}
            trackingName="login_form_submit_button"
          >
            {_t('Login')}
          </TrackedSubmit>
        )}
      </div>
    </Form>
  );
};

LoginForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'login',
  },
}))(LoginForm);
